"use client";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { iconsMap } from "@/components/icons";
import { Reveal } from "@/components/reveal";
import { PortableText } from "next-sanity";
import { ExtractBySectionType, SectionType } from "types";
import { SanityImage } from "@/components/sanity-image";
import { getURL } from "@/lib/utils";

export type ValuePropositionType = ExtractBySectionType<SectionType, "valueProposition", "_type">;

interface SolutionsSectionProps {
  index?: number;
  data: ValuePropositionType;
}

export function SolutionsValueProp({ data, index = 0 }: SolutionsSectionProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isEven = index % 2 === 0;
  const { title, subtitle, description, image, ctas } = data;

  return (
    <section className="py-8 bg-white">
      <div className="bg-white px-6 md:px-8 max-w-screen-2xl mx-auto">
        <div
          data-variant={isEven ? "primary" : "secondary"}
          className="max-w-screen-2xl mx-auto flex flex-col lg:flex-row items-center gap-10 p-6 lg:p-14 shadow-sm rounded-3xl data-[variant=secondary]:bg-secondary-50 data-[variant=primary]:bg-primary-50 lg:justify-between"
        >
          <Reveal className="flex-1 min-w-0 max-w-[600px] flex flex-col gap-4 order-2 lg:order-1">
            <h3 className="text-xl text-primary-text-900 md:text-3xl font-extrabold leading-[42px]">
              {title}
            </h3>
            <h4 className="text-sm md:text-lg leading-7">{subtitle}</h4>

            {description && (
              <PortableText
                value={description}
                components={{
                  block: {
                    normal: ({ children }) => (
                      <p className="font-normal text-xs sm:text-base text-primary-text-400 leading-7">
                        {children}
                      </p>
                    ),
                  },
                }}
              />
            )}

            <div className="pt-6 flex flex-row gap-4 w-full flex-wrap">
              {ctas?.map((cta, i) => {
                const href = getURL(cta);
                return (
                  <Button
                    key={i}
                    variant={cta.variant || "default"}
                    radius="lg"
                    className="w-fit"
                    size={isMobile ? "sm" : "xl"}
                    asChild
                  >
                    <Link
                      href={href}
                      target={cta.openInNewTab ? "_blank" : "_self"}
                      rel={cta.openInNewTab ? "noopener noreferrer" : undefined}
                    >
                      {cta.textBeforeIcon}
                      {cta.icon && iconsMap[cta.icon]}
                      {cta.textAfterIcon}
                    </Link>
                  </Button>
                );
              })}
            </div>
          </Reveal>

          <div className="flex-shrink-0 w-full max-w-sm sm:max-w-md xl:max-w-xl order-1 lg:order-2">
            <AspectRatio ratio={350 / 250} className="relative">
              {image && (
                <SanityImage
                  src={image}
                  placeholder="blur"
                  quality={60}
                  blurDataURL={image.asset?.metadata?.lqip || ""}
                  alt={title || "value proposition image"}
                  fill
                  className="object-cover rounded-2xl"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              )}
            </AspectRatio>
          </div>
        </div>
      </div>
    </section>
  );
}
